<template>
  <FlashNotifications ref="flash" />

  <!-- Trigger: profile (icon-only pill over banner, tooltip on hover) -->
  <button v-if="show_location === 'profile'" @click="openModal" type="button"
    class="group relative inline-flex items-center justify-center mt-2 mr-2 w-8 h-8 bg-white/90 dark:bg-[#1C2128]/90 backdrop-blur-sm border border-gray-200 dark:border-gray-600 rounded-full shadow-sm hover:bg-white dark:hover:bg-[#1C2128] hover:border-blue-300 dark:hover:border-blue-600 hover:shadow transition-all cursor-pointer">
    <svg class="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"/>
    </svg>
    <span class="pointer-events-none absolute bottom-full right-0 mb-2 whitespace-nowrap rounded-lg bg-gray-900 dark:bg-gray-700 px-2.5 py-1.5 text-xs font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-lg">
      Send a message to {{ host_name }}
    </span>
  </button>

  <!-- Trigger: no_events / message (solid button) -->
  <button v-else-if="show_location === 'no_events' || show_location === 'message'" @click="openModal" type="button"
    class="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm cursor-pointer">
    <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"/>
    </svg>
    Message {{ host_name }}
  </button>

  <!-- Modal -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0">
      <div v-if="showModal"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        @click.self="closeModal">

        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="closeModal"></div>

        <Transition
          enter-active-class="transition ease-out duration-200"
          enter-from-class="opacity-0 scale-95 translate-y-2"
          enter-to-class="opacity-100 scale-100 translate-y-0"
          leave-active-class="transition ease-in duration-150"
          leave-from-class="opacity-100 scale-100 translate-y-0"
          leave-to-class="opacity-0 scale-95 translate-y-2">
          <div v-if="showModal"
            class="relative w-full max-w-lg bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl overflow-hidden">

            <!-- Header -->
            <div class="flex items-start justify-between px-6 py-5 border-b border-gray-100 dark:border-gray-700">
              <div class="flex items-center gap-3">
                <div class="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                  <svg class="w-[18px] h-[18px] text-blue-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"/>
                  </svg>
                </div>
                <div>
                  <h3 class="text-base font-semibold text-gray-900 dark:text-white leading-tight">Message {{ host_name }}</h3>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ I18N.website_content.account_settings.send_message_subheading }}</p>
                </div>
              </div>
              <button @click="closeModal" type="button"
                class="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- Form -->
            <form @submit.prevent="submitMessage" class="px-6 py-5 space-y-4">

              <!-- Name -->
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                  Your name <span class="text-red-400 normal-case font-normal tracking-normal">*</span>
                </label>
                <input
                  ref="nameInput"
                  v-model="form.user_name"
                  type="text"
                  required
                  placeholder="Jane Smith"
                  :disabled="isPrefilled"
                  class="w-full px-3.5 py-2.5 text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-[#22272E] border border-gray-200 dark:border-gray-600 rounded-xl placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>

              <!-- Email -->
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                  Email address <span class="text-red-400 normal-case font-normal tracking-normal">*</span>
                </label>
                <input
                  v-model="form.email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  :disabled="isPrefilled"
                  class="w-full px-3.5 py-2.5 text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-[#22272E] border border-gray-200 dark:border-gray-600 rounded-xl placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>

              <!-- Message -->
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                  Message <span class="text-red-400 normal-case font-normal tracking-normal">*</span>
                </label>
                <textarea
                  ref="messageInput"
                  v-model="form.message"
                  required
                  rows="4"
                  placeholder="What would you like to say?"
                  class="w-full px-3.5 py-2.5 text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-[#22272E] border border-gray-200 dark:border-gray-600 rounded-xl placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
                ></textarea>
              </div>

              <!-- Actions -->
              <div class="flex items-center justify-end gap-3 pt-1">
                <button type="button" @click="closeModal"
                  class="px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-colors">
                  Cancel
                </button>
                <button type="submit" :disabled="submitting"
                  class="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-xl transition-colors">
                  <svg v-if="submitting" class="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  {{ submitting ? 'Sending...' : 'Send message' }}
                </button>
              </div>

            </form>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, nextTick } from 'vue'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import { useConstants } from 'composables/useConstants.js'
import { useRequest } from 'composables/useRequest.js'

const props = defineProps(['host_id', 'host_name', 'current_attendee', 'show_location'])

const { I18N } = useConstants()

const flash = ref(null)
const { axiosRequest } = useRequest(flash)

const showModal  = ref(false)
const nameInput  = ref(null)
const messageInput = ref(null)
const submitting = ref(false)
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function getAttendee() {
  if (props.current_attendee && props.current_attendee !== '') {
    try { return JSON.parse(props.current_attendee) } catch { return null }
  }
  return null
}

function freshForm() {
  const attendee = getAttendee()
  return {
    user_name: attendee?.full_name || '',
    email: attendee?.email || '',
    message: '',
    host_id: props.host_id,
    url: window.location.href,
  }
}

const form = ref(freshForm())
const isPrefilled = !!getAttendee()

function openModal() {
  showModal.value = true
  nextTick(() => {
    if (isPrefilled) {
      messageInput.value?.focus()
    } else {
      nameInput.value?.focus()
    }
  })
}

function closeModal() {
  showModal.value = false
  form.value = freshForm()
}

async function submitMessage() {
  if (!emailRegex.test(form.value.email)) {
    flash.value.show('Invalid email format', 'alert')
    return
  }
  if (!form.value.message.trim()) {
    flash.value.show('Message cannot be blank', 'alert')
    return
  }

  submitting.value = true
  try {
    form.value.url = window.location.href
    const res = await axiosRequest('POST', '/api/v1/messages', { message: form.value }, false)
    if (!res.js_error && res.response.success) {
      closeModal()
      flash.value.show(res.response.message, 'notice')
    }
  } finally {
    submitting.value = false
  }
}
</script>
